import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { adminMarketplaceStats, adminListWebhookErrors, adminResolveWebhookError, adminEmailHealth, adminWebVitalsSummary, adminListEmailFailures, adminRetryEmail, adminListCronRuns, adminReprocessWebhook } from "@/lib/admin.functions";
import { Loader2, AlertTriangle, Activity, Mail, TrendingUp, CheckCircle2, Gauge, RefreshCw, Clock } from "lucide-react";


export const Route = createFileRoute("/_authenticated/admin/ops")({
  head: () => ({ meta: [{ title: "Operations — Admin — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: AdminOps,
});

type Stats = {
  funnel: { jobs_posted: number; offers_sent: number; offers_accepted: number; bookings_paid: number; bookings_completed: number; bookings_disputed: number };
  timings: { median_seconds_to_accept: number; median_minutes_to_complete: number; median_hours_offer_to_completed: number };
  payouts: { pending: number; in_transit: number; paid: number; failed: number };
  webhook_health: { errors_open: number; errors_24h: number; events_processed_24h: number };
};
type WebhookError = { id: string; source: string; event_id: string | null; event_type: string | null; error_message: string; resolved: boolean; created_at: string };

function AdminOps() {
  const navigate = useNavigate();
  const fetchStats = useServerFn(adminMarketplaceStats);
  const fetchErrors = useServerFn(adminListWebhookErrors);
  const resolveErr = useServerFn(adminResolveWebhookError);
  const fetchEmail = useServerFn(adminEmailHealth);
  const fetchVitals = useServerFn(adminWebVitalsSummary);
  const fetchEmailFails = useServerFn(adminListEmailFailures);
  const fetchCron = useServerFn(adminListCronRuns);
  const retryEmail = useServerFn(adminRetryEmail);
  const reprocessHook = useServerFn(adminReprocessWebhook);

  const [days, setDays] = useState(30);
  const [stats, setStats] = useState<Stats | null>(null);
  const [errors, setErrors] = useState<WebhookError[]>([]);
  const [email, setEmail] = useState<{ counts: Record<string, number>; total: number } | null>(null);
  const [vitals, setVitals] = useState<{ rows: Array<{ path: string; metric: string; p75: number; samples: number }>; total: number } | null>(null);
  const [emailFails, setEmailFails] = useState<Array<{ id: string; recipient_email: string; template: string; status: string; error: string | null; created_at: string }>>([]);
  const [cronRuns, setCronRuns] = useState<Array<{ id: string; job_name: string; status: string; duration_ms: number | null; created_at: string; details: unknown }>>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, e, m, v, ef, cr] = await Promise.all([
        fetchStats({ data: { days } }),
        fetchErrors(),
        fetchEmail(),
        fetchVitals({ data: { hours: 24 } }),
        fetchEmailFails(),
        fetchCron(),
      ]);
      setStats(s.stats as Stats);
      setErrors(e.errors as WebhookError[]);
      setEmailFails(ef.rows as never);
      setCronRuns(cr.rows as never);
      setEmail(m);
      setVitals(v);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load");
    }
  }, [fetchStats, fetchErrors, fetchEmail, fetchVitals, fetchEmailFails, fetchCron, days]);


  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user) { navigate({ to: "/auth", search: { redirect: "/admin/ops" } }); return; }
      await load();
      setLoading(false);
    })();
  }, [navigate, load]);

  // Realtime: refresh when new webhook errors arrive
  useEffect(() => {
    const channel = supabase
      .channel("admin-ops")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "webhook_errors" }, () => void load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  async function onResolve(id: string) {
    try { await resolveErr({ data: { id } }); toast.success("Marked resolved"); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); }
  }
  async function onRetryEmail(id: string) {
    try { await retryEmail({ data: { id } }); toast.success("Email re-queued"); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Retry failed"); }
  }
  async function onReprocess(id: string) {
    try { const r = await reprocessHook({ data: { id } }); toast.success(r.hint ?? "Marked for replay"); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); }
  }

  if (loading) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!stats) return <div className="p-10 text-center text-muted-foreground">Access denied or no data.</div>;

  const f = stats.funnel;
  const conv = (a: number, b: number) => (b ? ((a / b) * 100).toFixed(1) + "%" : "—");

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Operations</h1>
            <p className="mt-1 text-sm text-muted-foreground">Marketplace health, webhook reliability, email delivery.</p>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map((d) => (
              <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>{d}d</Button>
            ))}
            <Button asChild size="sm" variant="outline"><Link to="/admin/disputes">Disputes</Link></Button>
            <Button asChild size="sm" variant="outline"><Link to="/admin/quality">Quality alerts</Link></Button>
            <Button asChild size="sm" variant="outline"><Link to="/admin/kyc">KYC queue</Link></Button>
            <Button asChild size="sm" variant="outline"><Link to="/admin/funnel">Funnel</Link></Button>

          </div>
        </div>

        {/* Webhook health */}
        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          <HealthCard
            icon={AlertTriangle}
            label="Open webhook errors"
            value={String(stats.webhook_health.errors_open)}
            tone={stats.webhook_health.errors_open > 0 ? "danger" : "ok"}
          />
          <HealthCard icon={Activity} label="Stripe events 24h" value={String(stats.webhook_health.events_processed_24h)} />
          <HealthCard
            icon={Mail}
            label="Emails sent 24h"
            value={String(email?.counts.sent ?? email?.counts.queued ?? 0)}
            sub={`${email?.counts.failed ?? 0} failed`}
            tone={(email?.counts.failed ?? 0) > 0 ? "warn" : "ok"}
          />
        </section>

        {/* Conversion funnel */}
        <section className="mt-10 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><TrendingUp className="h-5 w-5" /> Conversion funnel — last {days} days</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-6">
            <FunnelStep label="Jobs posted" value={f.jobs_posted} pct="100%" />
            <FunnelStep label="Offers sent" value={f.offers_sent} pct={conv(f.offers_sent, f.jobs_posted)} />
            <FunnelStep label="Offers accepted" value={f.offers_accepted} pct={conv(f.offers_accepted, f.offers_sent)} />
            <FunnelStep label="Bookings paid" value={f.bookings_paid} pct={conv(f.bookings_paid, f.offers_accepted)} />
            <FunnelStep label="Completed" value={f.bookings_completed} pct={conv(f.bookings_completed, f.bookings_paid)} />
            <FunnelStep label="Disputed" value={f.bookings_disputed} pct={conv(f.bookings_disputed, f.bookings_paid)} tone={f.bookings_disputed > 0 ? "warn" : "ok"} />
          </div>
        </section>

        {/* Lifecycle timings + payouts */}
        <section className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h3 className="font-display text-lg font-semibold">Median lifecycle timings</h3>
            <dl className="mt-4 space-y-2 text-sm">
              <Row label="Job posted → offer accepted" value={fmtSeconds(stats.timings.median_seconds_to_accept)} />
              <Row label="Booking start → completion" value={`${Math.round(stats.timings.median_minutes_to_complete)} min`} />
              <Row label="Offer → completion (end-to-end)" value={`${stats.timings.median_hours_offer_to_completed.toFixed(1)} hrs`} />
            </dl>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h3 className="font-display text-lg font-semibold">Payouts</h3>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Row label="Pending" value={String(stats.payouts.pending)} />
              <Row label="In transit" value={String(stats.payouts.in_transit)} />
              <Row label="Paid" value={String(stats.payouts.paid)} />
              <Row label="Failed" value={String(stats.payouts.failed)} tone={stats.payouts.failed > 0 ? "danger" : "ok"} />
            </dl>
          </div>
        </section>
        {/* Web Vitals RUM */}
        <section className="mt-10 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><Gauge className="h-5 w-5" /> Core Web Vitals (p75, last 24h)</h2>
          <p className="mt-1 text-xs text-muted-foreground">Real-user data from {vitals?.total ?? 0} samples. LCP &lt; 2500ms, INP &lt; 200ms, CLS &lt; 0.1 = good.</p>
          {!vitals || vitals.rows.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No samples yet — visit some public pages to collect data.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-muted-foreground"><th className="pb-2">Path</th><th className="pb-2">Metric</th><th className="pb-2 text-right">p75</th><th className="pb-2 text-right">Samples</th></tr></thead>
                <tbody>
                  {vitals.rows.slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-2 font-mono text-xs">{r.path}</td>
                      <td className="py-2">{r.metric}</td>
                      <td className={`py-2 text-right tabular-nums ${vitalTone(r.metric, r.p75)}`}>{formatVital(r.metric, r.p75)}</td>
                      <td className="py-2 text-right text-muted-foreground tabular-nums">{r.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>


        {/* Webhook errors */}
        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><AlertTriangle className="h-5 w-5" /> Webhook errors</h2>
          {errors.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No webhook errors recorded. 🎉</p>
          ) : (
            <ul className="mt-4 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
              {errors.map((e) => (
                <li key={e.id} className="flex flex-wrap items-start justify-between gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs uppercase">{e.source}</span>
                      <span>{e.event_type ?? "unknown"}</span>
                      {e.resolved && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" />resolved</span>}
                    </p>
                    <p className="mt-1 break-all text-xs text-muted-foreground">{e.event_id} · {new Date(e.created_at).toLocaleString("en-GB")}</p>
                    <p className="mt-2 text-sm text-destructive">{e.error_message}</p>
                  </div>
                  <div className="flex gap-2">
                    {!e.resolved && <Button size="sm" variant="outline" onClick={() => onResolve(e.id)}>Mark resolved</Button>}
                    {!e.resolved && <Button size="sm" variant="outline" onClick={() => onReprocess(e.id)}><RefreshCw className="mr-1 h-3 w-3" />Reprocess</Button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Email delivery failures */}
        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><Mail className="h-5 w-5" /> Email delivery issues</h2>
          {emailFails.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No failed or queued emails. 🎉</p>
          ) : (
            <ul className="mt-4 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
              {emailFails.map((r) => (
                <li key={r.id} className="flex flex-wrap items-start justify-between gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{r.template} <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs uppercase">{r.status}</span></p>
                    <p className="mt-1 break-all text-xs text-muted-foreground">{r.recipient_email} · {new Date(r.created_at).toLocaleString("en-GB")}</p>
                    {r.error && <p className="mt-1 text-xs text-destructive">{r.error}</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onRetryEmail(r.id)}><RefreshCw className="mr-1 h-3 w-3" />Retry</Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Background jobs */}
        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><Clock className="h-5 w-5" /> Background jobs</h2>
          <p className="mt-1 text-xs text-muted-foreground">sweep-stuck-bookings every 30 min · retry-failed-payouts daily at 09:00 UTC</p>
          {cronRuns.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No runs recorded yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card text-sm">
              {cronRuns.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <p className="font-medium">{r.job_name} · <span className={r.status === "ok" ? "text-emerald-700" : "text-amber-700"}>{r.status}</span></p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")} · {r.duration_ms ?? "—"}ms</p>
                  </div>
                  <code className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded bg-muted px-2 py-1 text-[11px]">{JSON.stringify(r.details ?? {})}</code>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function fmtSeconds(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} hrs`;
}

function formatVital(metric: string, v: number): string {
  if (metric === "CLS") return v.toFixed(3);
  return `${Math.round(v)} ms`;
}
function vitalTone(metric: string, v: number): string {
  const thresholds: Record<string, [number, number]> = { LCP: [2500, 4000], INP: [200, 500], FCP: [1800, 3000], TTFB: [800, 1800], CLS: [0.1, 0.25] };
  const t = thresholds[metric]; if (!t) return "";
  if (v <= t[0]) return "text-emerald-600";
  if (v <= t[1]) return "text-amber-600";
  return "text-red-600 font-semibold";
}




function HealthCard({ icon: Icon, label, value, sub, tone = "ok" }: { icon: typeof AlertTriangle; label: string; value: string; sub?: string; tone?: "ok" | "warn" | "danger" }) {
  const toneCls = tone === "danger" ? "border-red-200 bg-red-50" : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-border bg-card";
  return (
    <div className={`flex items-center gap-4 rounded-2xl border p-5 shadow-card ${toneCls}`}>
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-background"><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="font-display text-2xl font-bold">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

function FunnelStep({ label, value, pct, tone = "ok" }: { label: string; value: number; pct: string; tone?: "ok" | "warn" }) {
  const toneCls = tone === "warn" ? "bg-amber-50 border-amber-200" : "bg-background border-border";
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold">{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{pct}</p>
    </div>
  );
}

function Row({ label, value, tone = "ok" }: { label: string; value: string; tone?: "ok" | "danger" }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`font-semibold ${tone === "danger" ? "text-destructive" : ""}`}>{value}</dd>
    </div>
  );
}
